import {
  Box,
  Button,
  Modal,
  Card,
  CardContent,
  IconButton,
  Typography,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Divider,
  Radio,
  RadioGroup,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import { modalStyle } from "../../../../_utils/modalStyle";
import { HighlightOff } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import peopleAPI from "../../../../_api/_people";
import UsersAPI from "../../../../_api/_usersAPI";
import { useSelector } from "react-redux";
import axios from "axios";

const AdminUserRoleModal: React.FC<{
  open: boolean;
  close: () => void;
  reloadCall: () => void;
  users: any;
}> = ({ open, close, users, reloadCall }) => {
  const { t } = useTranslation("common");
  const context = useSelector((state: any) => state.auth.context);
  const [loading, setLoading] = useState(false);
  const hasCre = useSelector(
    (state: any) => state.home?.paramjson?.menu?.third_party
  );
  const [roles, setRoles] = useState({
    rh: false,
    manager: false,
    admin: false,
    adminRh: false,
    recruiter: false,
    adminRecruiter: false,
    advisor: false,
    adminAdvisor: false,
    ces: false,
    ...(hasCre ? { cre: false } : {}),
  });
  const [roleError, setRoleError] = useState(false);

  const deleteUser = () => {
    setLoading(true);
    const calls: any = [];
    users?.map((user: any) => calls.push(peopleAPI.delete(user.id)));
    Promise.all(calls)
      .then((res: any) => {
        reloadCall();
        setLoading(false);
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleSave = async () => {
    const hasRole = Object.values(roles).some(Boolean);
    if (!hasRole) {
      setRoleError(true);
      setLoading(false);
      return;
    }
    setRoleError(false);
    setLoading(true);
    const calls: any = [];
    let userId: any;
    if (users?.length === 1 && !users?.[0]?.userId) {
      try {
        const res = await axios.get(
          peopleAPI.getById(users?.[0]?.person["@id"].split("/")[3])
        );
        userId = res?.data?.userId;
      } catch (err) {
        setLoading(false);
      }
    }
    // eslint-disable-next-line array-callback-return
    users?.map(async (user: any) => {
      const newRoles = [];
      user?.roles?.includes("ROLE_COLLAB") && newRoles.push("ROLE_COLLAB");
      roles.rh && newRoles.push("ROLE_RH");
      roles.manager && newRoles.push("ROLE_MANAGER");
      roles.admin && newRoles.push("ROLE_ADMIN");
      roles.adminRh && newRoles.push("ROLE_ADMINRH");
      roles.recruiter && newRoles.push("ROLE_RECRUITER");
      roles.adminRecruiter && newRoles.push("ROLE_ADMINRECRUITER");
      roles.advisor && newRoles.push("ROLE_ADVISOR");
      roles.adminAdvisor && newRoles.push("ROLE_ADMINADVISOR");
      roles.cre && newRoles.push("ROLE_CRE");
      roles.ces &&
        newRoles.push("ROLE_CES", "ROLE_ADVISOR", "ROLE_ADMINADVISOR");
      console.log("ROLE", newRoles);
      calls.push(UsersAPI.put(user.userId ?? userId, { roles: newRoles }));
    });
    handleClose();
    Promise.all(calls)
      .then((res: any) => {
        reloadCall();
        setLoading(false);
        handleClose();
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    setRoles({
      rh: false,
      manager: false,
      admin: false,
      adminRh: false,
      recruiter: false,
      adminRecruiter: false,
      advisor: false,
      adminAdvisor: false,
      ces: false,
      ...(hasCre ? { cre: false } : {}),
    });
    close();
  };

  useEffect(() => {
    users &&
      setRoles({
        rh: users?.length === 1 ? users?.[0]?.roles.includes("ROLE_RH") : false,
        manager:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_MANAGER")
            : false,
        admin:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_ADMIN")
            : false,
        adminRh:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_ADMINRH")
            : false,
        recruiter:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_RECRUITER")
            : false,
        adminRecruiter:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_RECRUITER")
            : false,
        advisor:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_ADVISOR")
            : false,
        adminAdvisor:
          users?.length === 1
            ? users?.[0]?.roles.includes("ROLE_ADMINADVISOR")
            : false,
        ...(hasCre
          ? {
              cre:
                users?.length === 1
                  ? users?.[0]?.roles.includes("ROLE_CRE")
                  : false,
            }
          : {}),
        ces:
          users?.length === 1 ? users?.[0]?.roles.includes("ROLE_CES") : false,
      });
  }, [hasCre, users]);

  const CreInput = useMemo(
    () => () =>
      hasCre ? (
        <FormControlLabel
          value={roles.cre}
          onChange={(e: any) => setRoles({ ...roles, cre: !roles.cre })}
          control={<Checkbox checked={roles.cre} />}
          label={t("roles.cre")}
        />
      ) : null,
    [setRoles, hasCre, roles, t]
  );

  return (
    <Modal open={open}>
      <Card
        sx={{
          ...modalStyle,
          width: { xs: "95%", sm: "400px" },
        }}
      >
        <Box sx={{ width: "100%", display: "flex" }}>
          <IconButton
            onClick={handleClose}
            sx={{ ml: "auto", mt: 1, mr: 1 }}
            aria-label="close"
          >
            <HighlightOff />
          </IconButton>
        </Box>
        <CardContent sx={{ mt: -6 }}>
          <Typography variant="h4">{t("admin.params.roles.handle")}</Typography>
          <FormGroup sx={{ mt: 3 }}>
            {context === "recruiter" ? (
              <>
                <FormControlLabel
                  disabled
                  value={roles.recruiter}
                  onChange={(e: any) =>
                    setRoles({ ...roles, recruiter: !roles.recruiter })
                  }
                  control={<Checkbox checked={roles.recruiter} disabled />}
                  label={t("roles.recruiter")}
                />
                <FormControlLabel
                  value={roles.adminRecruiter}
                  onChange={(e: any) =>
                    setRoles({
                      ...roles,
                      adminRecruiter: !roles.adminRecruiter,
                    })
                  }
                  control={<Checkbox checked={roles.adminRecruiter} />}
                  label={t("roles.adminrecruiter")}
                />
                <CreInput />
              </>
            ) : context === "advisor" ? (
              <>
                <RadioGroup
                  value={roles.advisor ? "advisor" : roles.ces ? "ces" : ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "ces") {
                      setRoles({
                        ...roles,
                        advisor: false,
                        ces: true,
                        adminAdvisor: false,
                        cre: false,
                      });
                    } else {
                      setRoles({
                        ...roles,
                        advisor: true,
                        ces: false,
                      });
                    }
                  }}
                >
                  <FormControlLabel
                    value="advisor"
                    control={<Radio />}
                    label={t("roles.advisor")}
                  />
                  <FormControlLabel
                    value="ces"
                    control={<Radio />}
                    label={t("roles.ces")}
                  />
                </RadioGroup>
                <Divider sx={{ my: 2 }} />
                {!roles.ces && (
                  <>
                    <FormControlLabel
                      value={roles.adminAdvisor}
                      onChange={(e: any) =>
                        setRoles({
                          ...roles,
                          adminAdvisor: !roles.adminAdvisor,
                        })
                      }
                      control={<Checkbox checked={roles.adminAdvisor} />}
                      label={t("roles.adminadvisor")}
                    />
                    <CreInput />
                  </>
                )}
              </>
            ) : (
              <>
                <FormControlLabel
                  value={roles.rh}
                  onChange={(e: any) => setRoles({ ...roles, rh: !roles.rh })}
                  control={<Checkbox checked={roles.rh} />}
                  label={t("roles.rh")}
                />
                <FormControlLabel
                  value={roles.manager}
                  onChange={(e: any) =>
                    setRoles({ ...roles, manager: !roles.manager })
                  }
                  control={<Checkbox checked={roles.manager} />}
                  label={t("roles.manager")}
                />
                <FormControlLabel
                  value={roles.admin}
                  onChange={(e: any) =>
                    setRoles({ ...roles, admin: !roles.admin })
                  }
                  control={<Checkbox checked={roles.admin} />}
                  label={t("roles.admin")}
                />
                <FormControlLabel
                  value={roles.adminRh}
                  onChange={(e: any) =>
                    setRoles({ ...roles, adminRh: !roles.adminRh })
                  }
                  control={<Checkbox checked={roles.adminRh} />}
                  label={t("roles.adminrh")}
                />
                <CreInput />
              </>
            )}
          </FormGroup>
          {roleError && (
            <Typography variant="body2" color="error">
              {t("admin.params.roles.error")}
            </Typography>
          )}
          {context === "admin" && (
            <Box
              display="flex"
              justifyContent="center"
              width="100%"
              sx={{ mt: 4 }}
            >
              <Button
                onClick={() => deleteUser()}
                disabled={loading}
                variant="contained"
                color="error"
                sx={{ height: "40px", width: "100%", m: 1 }}
              >
                <Typography variant="body2">
                  {t("admin.params.roles.delete")}
                </Typography>
              </Button>
            </Box>
          )}
          <Box display="flex" justifyContent="space-around" width="100%">
            <Button
              sx={{ height: "40px", width: "150px", m: 1 }}
              onClick={() => handleClose()}
              variant="outlined"
            >
              <Typography variant="body2">{t("btn.cancel")}</Typography>
            </Button>
            <Button
              disabled={loading}
              sx={{ height: "40px", width: "150px", m: 1 }}
              onClick={() => handleSave()}
              variant="contained"
            >
              <Typography variant="body2">{t("btn.save")}</Typography>
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Modal>
  );
};

export default AdminUserRoleModal;
